import { useEffect, useRef, useState } from 'preact/hooks';
import { useToggle } from 'usehooks-ts';
import { CommentsService } from '@ts/services/commentsService';
import { url } from '@ts/utils/url';
import { useDataComponent } from '../preact';
import { Button } from '../../showcase-ui/Button/Button';
import { Comments } from './Comments';
import { InfoBanner } from '../InfoBanner/InfoBanner';
import { animateAnchor } from './comment';

type Props = {
  form?: string;
  authenticator?: string;
  commentsService?: CommentsService;
};

/**
 * Displays a Button to `show` or `hide` comments on the article.\
 * It's responsible for fetching data and displaying appropriate `Info Banners`.
 */
export const CommentsWrapper = ({
  form,
  authenticator,
  commentsService = new CommentsService(),
}: Props) => {
  const buttonRef = useRef<HTMLDivElement>(null);

  const [deactivatedMessage, setDeactivatedMessage] = useState<string>();
  const [isUserModerated, setIsUserModerated] = useState<boolean>();

  const [isShowing, toggleIsShowing] = useToggle(false);
  const [comments, setComments] = useState<string>();
  const [canComment, setCanComment] = useState<boolean>();

  const getDiscussionStatus = async (service: CommentsService) => {
    try {
      const { allowed, message } = await service.getDiscussionStatus();
      if (allowed) return;

      setDeactivatedMessage(() => message);
    } catch (error) {
      if (error instanceof Error)
        console.error(`Error getting discussion status: ${error.message}`);
    }
  };

  const getComments = async (service: CommentsService) => {
    try {
      const comments = await service.getComments();
      setComments(() => comments);

      toggleIsShowing();
    } catch (error) {
      if (error instanceof Error)
        console.error(`Error getting comments: ${error.message}`);
    }
  };

  const showComments = async (service: CommentsService) => {
    try {
      const { joinCommunity, isModeratedUser, isActiveUser, canSeeComments } =
        await service.getUserInfo();

      // Redirect user to join community to be able to comment
      if (joinCommunity)
        window.location.href = `${url.getPortalUrl()}/@@join-community`;

      // Moderate user cannot write comment
      if (isModeratedUser) setIsUserModerated(() => true);

      // Only active users can write comments
      if (isActiveUser && !isModeratedUser) setCanComment(() => true);

      // Show comments if user is allowed to see comments
      if (canSeeComments) getComments(service);
    } catch (error) {
      if (error instanceof Error)
        console.error(`Error getting user information: ${error.message}`);
    }
  };

  const handleClick = async () => {
    if (isShowing) toggleIsShowing();
    if (!isShowing) showComments(commentsService);
  };

  // TODO: Fix scroll to comment functionality
  const scrollToNewComment = () => {
    const locationHash = window.location.hash;
    if (!locationHash) return;

    // Display comments before scrolling to new comment
    buttonRef.current?.click();

    // Scroll to to the comments section
    animateAnchor('#comments', 1000);

    // Scroll to new comment
    animateAnchor(locationHash, 1000);
    setTimeout(animateAnchor, 700, locationHash);
  };

  useEffect(() => {
    scrollToNewComment();
  }, []);

  useEffect(() => {
    getDiscussionStatus(commentsService);
  }, [deactivatedMessage]);

  return (
    <>
      {deactivatedMessage ? (
        <InfoBanner
          type="lock"
          title="Kommentarfunktion deaktiviert"
          content={deactivatedMessage}
        />
      ) : (
        <div
          ref={buttonRef}
          onClick={handleClick}
          style={{ maxWidth: 'fit-content' }}
        >
          <Button
            label={`Kommentare ${isShowing ? 'ausblenden' : 'einblenden'}`}
            className="bo-btn--md"
            type="button"
          />
        </div>
      )}

      {isShowing && (
        <>
          {isUserModerated && (
            <InfoBanner
              type="info"
              title="Kommentarfunktion deaktiviert"
              content="Die Kommentare unter diesem Beitrag haben den Bereich des guten Tons verlassen. Deshalb wurde der Kommentarbereich geschlossen."
              marginTop="var(--DF-PRIM-SPACING-48)"
            />
          )}

          {canComment && comments && (
            <Comments
              form={form}
              authenticator={authenticator}
              canComment={canComment}
              comments={comments}
              service={commentsService}
            />
          )}
        </>
      )}
    </>
  );
};

// TODO: remove dataset attributes when api endpoints are ready
useDataComponent('.pts-comments', dataset => {
  const { form, authenticator } = dataset;

  // Clear the dataset to prevent mutation and memory leaks
  Object.keys(dataset).map(key => delete dataset[key]);

  return <CommentsWrapper form={form} authenticator={authenticator} />;
});
